import { Copy, AlertTriangle } from "lucide-react";
import { useLang } from "../i18n";

const Block = ({ title, color, children, testid }) => (
  <div data-testid={testid} className="border-[3px] border-black rounded-xl bg-white p-4 shadow-[4px_4px_0px_0px_#000]">
    <p className="text-[11px] font-bold uppercase tracking-[0.12em] mb-2" style={{ color }}>{title}</p>
    {children}
  </div>
);

const List = ({ items }) => (
  <ul className="space-y-1.5">
    {items.map((x, i) => (
      <li key={i} className="text-sm font-medium text-[#222] leading-relaxed flex gap-2">
        <span className="shrink-0 font-bold">•</span><span>{x}</span>
      </li>
    ))}
  </ul>
);

const Sub = ({ label, items }) => items?.length ? (
  <div className="mt-2">
    <p className="text-xs font-bold text-[#444] mb-1">{label}</p>
    <List items={items} />
  </div>
) : null;

export const convoToText = (r, t) => {
  const sec = (title, arr) => (arr?.length ? `\n${title}\n${arr.map((x) => `• ${x}`).join("\n")}\n` : "");
  const o = r.overview, e = r.emotional, n = r.next_steps;
  return [
    `PRAPSY · ${t.cv_btn}`,
    `\n${t.cv_overview}\n${o.summary}\n${o.topic ? `${t.cv_topic}: ${o.topic}\n` : ""}${o.dynamic}`,
    sec(t.cv_tensions, o.tensions), sec(t.cv_facts, r.facts), sec(t.cv_interp, r.interpretations),
    sec(t.cv_patterns, r.patterns), sec(t.cv_thoughts, e.thoughts), sec(t.cv_emotions, e.emotions),
    sec(t.cv_distortions, e.distortions), e.note ? `\n${e.note}\n` : "", sec(t.cv_unknowns, r.unknowns),
    sec(t.cv_persp, r.perspectives), sec(t.cv_suggest, n.suggestions), sec(t.cv_replies, n.reply_ideas),
    sec(t.cv_selfq, n.self_questions), r.closing ? `\n${r.closing}` : "",
  ].join("");
};

export const ConversationResult = ({ result, noFilter, onCopy }) => {
  const { t } = useLang();
  const o = result.overview, e = result.emotional, n = result.next_steps;
  return (
    <div className="space-y-4" data-testid="conversation-result">
      {noFilter && (
        <span className="inline-block bg-[#FF3366] text-white border-[2px] border-black rounded-full px-3 py-1 text-xs font-bold uppercase">{t.no_filter_ribbon}</span>
      )}
      {result.reading_issues?.length > 0 && (
        <div data-testid="cv-reading-issues" className="border-[3px] border-black rounded-xl bg-[#FFD500] p-4">
          <p className="text-[11px] font-bold uppercase tracking-[0.12em] mb-2 flex items-center gap-1.5"><AlertTriangle size={14} strokeWidth={2.5} /> {t.pr_issues}</p>
          <List items={result.reading_issues} />
        </div>
      )}
      <Block title={t.cv_overview} color="#BD00FF" testid="cv-overview">
        {o.topic && <p className="text-xs font-bold uppercase bg-[#FFD500] border-[2px] border-black rounded-full px-2.5 py-0.5 inline-block mb-2">{o.topic}</p>}
        <p className="text-sm font-semibold text-[#222] leading-relaxed">{o.summary}</p>
        {o.dynamic && <p className="text-sm font-medium text-[#444] leading-relaxed mt-2">{o.dynamic}</p>}
        <Sub label={t.cv_tensions} items={o.tensions} />
      </Block>
      <div className="grid sm:grid-cols-2 gap-4">
        <Block title={t.cv_facts} color="#008F5A" testid="cv-facts"><List items={result.facts} /></Block>
        <Block title={t.cv_interp} color="#D97706" testid="cv-interpretations"><List items={result.interpretations} /></Block>
      </div>
      {result.patterns?.length > 0 && <Block title={t.cv_patterns} color="#000" testid="cv-patterns"><List items={result.patterns} /></Block>}
      <Block title={t.cv_emotional} color="#BD00FF" testid="cv-emotional">
        <Sub label={t.cv_thoughts} items={e.thoughts} />
        <Sub label={t.cv_emotions} items={e.emotions} />
        <Sub label={t.cv_distortions} items={e.distortions} />
        {e.note && <p className="mt-3 text-sm font-semibold bg-[#F4F0E6] border-[2px] border-black rounded-lg p-2.5">{e.note}</p>}
      </Block>
      <Block title={t.cv_unknowns} color="#FF3366" testid="cv-unknowns"><List items={result.unknowns} /></Block>
      <Block title={t.cv_persp} color="#000" testid="cv-perspectives"><List items={result.perspectives} /></Block>
      <div data-testid="cv-next-steps" className="border-[3px] border-black rounded-xl bg-black text-white p-4 shadow-[4px_4px_0px_0px_#00FFA3]">
        <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#00FFA3] mb-2">{t.cv_next}</p>
        {[[t.cv_suggest, n.suggestions], [t.cv_replies, n.reply_ideas], [t.cv_selfq, n.self_questions]].map(([label, items]) => items?.length ? (
          <div key={label} className="mt-2">
            <p className="text-xs font-bold text-[#FFD500] mb-1">{label}</p>
            <ul className="space-y-1.5">{items.map((x, i) => <li key={i} className="text-sm leading-relaxed flex gap-2"><span className="font-bold">•</span><span>{x}</span></li>)}</ul>
          </div>
        ) : null)}
      </div>
      {result.transcript && (
        <details data-testid="cv-transcript" className="border-[3px] border-black rounded-xl bg-white p-4">
          <summary className="text-[11px] font-bold uppercase tracking-[0.12em] cursor-pointer">{t.pr_transcript}</summary>
          <pre className="mt-2 whitespace-pre-wrap font-sans text-sm text-[#222]">{result.transcript}</pre>
        </details>
      )}
      {result.closing && <p data-testid="cv-closing" className="text-center font-display font-bold text-lg leading-snug px-2">"{result.closing}"</p>}
      {onCopy && (
        <button data-testid="cv-copy-btn" onClick={onCopy}
          className="w-full flex items-center justify-center gap-2 bg-white border-[3px] border-black rounded-xl py-3 font-bold uppercase text-sm tracking-wide shadow-[4px_4px_0px_0px_#000] transition-[transform,box-shadow] duration-100 hover:shadow-[2px_2px_0px_0px_#000] hover:translate-x-[2px] hover:translate-y-[2px]">
          <Copy size={16} strokeWidth={2.5} /> {t.cv_copy}
        </button>
      )}
    </div>
  );
};
